import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Filet de sécurité de dernier recours.
 *
 * Sans elle, une exception levée pendant le rendu démonte tout l'arbre React et
 * laisse une page blanche, sans message ni indication de ce qui a échoué. Le
 * repli est volontairement écrit en styles en ligne : il doit s'afficher même
 * si l'erreur vient d'un fournisseur de contexte, de la feuille de style ou du
 * thème, donc sans rien supposer de ce qui est monté au-dessus.
 */
interface Props {
    children: ReactNode;
}

interface State {
    error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
    state: State = { error: null };

    static getDerivedStateFromError(error: Error): State {
        return { error };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        console.error('Erreur non rattrapée dans le rendu', error, info.componentStack);
    }

    render() {
        if (!this.state.error) return this.props.children;

        return (
            <FatalError
                title="L'application n'a pas pu s'afficher"
                error={this.state.error}
            />
        );
    }
}

/**
 * Écran d'échec autonome, également utilisé par `main.tsx` lorsque l'amorçage
 * échoue avant que React ne soit monté.
 */
export function FatalError({ title, error }: { title: string; error: unknown }) {
    const message =
        error instanceof Error ? error.message : String(error ?? 'Erreur inconnue');

    return (
        <div
            style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '1.5rem',
                fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
                background: '#f8fafc',
                color: '#0f172a',
            }}
        >
            <div style={{ maxWidth: '32rem' }}>
                <h1 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                    {title}
                </h1>
                <p style={{ marginBottom: '1rem', lineHeight: 1.6, color: '#475569' }}>
                    Le détail ci-dessous aide à identifier la cause. La console du
                    navigateur en contient la trace complète.
                </p>
                <pre
                    style={{
                        background: '#0f172a',
                        color: '#e2e8f0',
                        padding: '0.875rem',
                        borderRadius: '0.5rem',
                        fontSize: '0.8125rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        marginBottom: '1rem',
                    }}
                >
                    {message}
                </pre>
                <button
                    type="button"
                    onClick={() => window.location.reload()}
                    style={{
                        background: '#2563eb',
                        color: '#fff',
                        border: 0,
                        borderRadius: '0.5rem',
                        padding: '0.625rem 1.125rem',
                        fontSize: '0.9375rem',
                        cursor: 'pointer',
                    }}
                >
                    Recharger
                </button>
            </div>
        </div>
    );
}
